import { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Descriptions,
  message,
  Select,
  Spin,
} from "antd";
import { PlusOutlined, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "./../json/appConstant.json";
import FilterComponent from "./FilterComponent";

const { Option } = Select;

const Users = ({ isDarkMode }) => {
  const [form] = Form.useForm();
  const [usersData, setUsersData] = useState([]);
  const [departmentsData, setDepartmentsData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null });
  const { jsonApi } = useApis();

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize, search);
    // fetchDepartments();
  }, [pagination.current, pagination.pageSize, search, dateFilter]);

  const fetchUsers = async (
    page,
    pageSize,
    search = "",
    sortField = "",
    sortOrder = ""
  ) => {
    setIsDataLoading(true);

    const params = {
      page,
      pageSize,
      search,
      sortField,
      sortOrder,
      ...(dateFilter.startDate && { startDate: dateFilter.startDate }),
      ...(dateFilter.endDate && { endDate: dateFilter.endDate })
    };

    // Fetch the data from the API with search and sort parameters
    const response = await jsonApi(appConstant.User, params, {}, null);

    // Check if the API response is successful
    if (response.data.code === 200 && response.data.status === "success") {
      setUsersData(
        response.data.data.users.map((user) => ({
          key: user.id,
          id: user.id,
          name: user.name,
          email: user.email,
          phone: user.phone,
          // department_id: user.department_id,
          // department_name: user.department_name,
          createdAt: user.createdAt,
        }))
      );

      setPagination((prevPagination) => ({
        ...prevPagination,
        total: response.data.data.totalUsers,
      }));
    }

    setIsDataLoading(false);
  };

  // const fetchDepartments = async () => {
  //   const response = await jsonApi(appConstant.Department, {}, null, null);
  //   if (response.data.code === 200 && response.data.status === "success") {
  //     setDepartmentsData(response.data.data);
  //   }
  // };

  const showModal = (mode, user = null) => {
    setModalMode(mode);
    setSelectedUser(user);
    setIsModalVisible(true);

    if (mode === "view" && user) {
      // View mode - no form updates needed
    } else if (mode === "edit" && user) {
      // Set form values for edit mode
      form.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phone,
        // department_id: user.department_id,
      });
    } else if (mode === "add") {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (modalMode === "add") {
        const response = await jsonApi(
          appConstant.createUser,
          null,
          {
            name: values.name,
            email: values.email,
            phone: values.phone,
            password: values.password,
            // department_id: values.department_id,
          },
          null
        );

        if (response.data) {
          message.success("User added successfully");
          fetchUsers(pagination.current, pagination.pageSize);
          setIsModalVisible(false);
        } else {
          message.error(response.message);
        }
      } else if (modalMode === "edit") {
        const response = await jsonApi(
          appConstant.updateUser,
          { id: selectedUser.id },
          {
            name: values.name,
            email: values.email,
            phone: values.phone,
            // department_id: values.department_id,
          },
          "put"
        );

        if (response?.data?.code === 200) {
          message.success("User updated successfully");
          fetchUsers(pagination.current, pagination.pageSize);
          setIsModalVisible(false);
          form.resetFields();
        } else {
          message.error(response?.data?.message || "Failed to update user");
        }
      }
    } catch (error) {
      console.error("Submit Error:", error);
      message.error("An error occurred while processing your request");
    }
    setLoading(false);
  };

  const handleDelete = async (userId) => {
    Modal.confirm({
      title: "Delete Confirmation",
      content: "Are you sure you want to delete this user?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          const response = await jsonApi(
            appConstant.deleteUser,
            { id: userId },
            null,
            "delete"
          );

          console.log("Delete Response:", response);

          if (response?.data?.code === 200 || response?.status === 200) {
            message.success("User deleted successfully");
            setUsersData((prevUsers) =>
              prevUsers.filter((user) => user.id !== userId)
            );
          } else {
            message.error(response.message || "Failed to delete user");
          }
        } catch (error) {
          console.error("Delete User Error:", error);
          message.error("An error occurred while deleting the user");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "S.No",
      key: "serialNumber",
      render: (_, __, index) => {
        const { current, pageSize } = pagination;
        return (current - 1) * pageSize + index + 1;
      },
      width: "5%",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => {
        // Check for undefined or null before calling localeCompare
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => {
        const emailA = a.email || "";
        const emailB = b.email || "";
        return emailA.localeCompare(emailB);
      },
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      sorter: (a, b) => {
        const phoneA = a.phone || "";
        const phoneB = b.phone || "";
        return phoneA.localeCompare(phoneB);
      },
    },
    // {
    //   title: "Department",
    //   dataIndex: "department_name",
    //   key: "department_name",
    //   sorter: (a, b) => {
    //     const deptA = a.department_name || '';
    //     const deptB = b.department_name || '';
    //     return deptA.localeCompare(deptB);
    //   },
    // },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => {
        // Ensure that createdAt values are valid dates
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateA - dateB;
      },
      render: (date) => new Date(date).toLocaleDateString(),
      width: "13%"
    },
    {
      title: "Actions",
      key: "action",
      render: (_, record) => (
        <Space
          size={4} // Reduce space between buttons
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Button
            type="link"
            onClick={() => showModal("view", record)}
            style={{ margin: 0 }}
          >
            View
          </Button>
          <Button
            type="link"
            onClick={() => showModal("edit", record)}
            style={{ margin: 0 }}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDelete(record.id)}
            style={{ margin: 0 }}
          >
            Delete
          </Button>
        </Space>
      ),
      width: "20%",
      align: "center",
    },
  ];

  const renderModalContent = () => {
    if (modalMode === "view" && selectedUser) {
      return (
        <Descriptions bordered column={1}>
          <Descriptions.Item label="Name">
            {selectedUser?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {selectedUser?.email}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {selectedUser?.phone}
          </Descriptions.Item>
          {/* <Descriptions.Item label="Department">
            {selectedUser?.department_name}
          </Descriptions.Item> */}
          <Descriptions.Item label="Created At">
            {new Date(selectedUser?.createdAt).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      );
    }

    return (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ status: true }}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[
            { required: true, message: "Please input the name" },
            {
              pattern: /^[A-Za-z\s]+$/,
              message: "Name can only contain letters and spaces",
            },
          ]}
        >
          <Input placeholder="Enter name" maxLength={50} />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, message: "Please input the email" }]}
        >
          <Input placeholder="Enter email" maxLength={50} />
        </Form.Item>

        {modalMode === "add" && (
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: "Please input your password" }]}
          >
            <Input.Password placeholder="Enter password" maxLength={20} />
          </Form.Item>
        )}

        <Form.Item
          name="phone"
          label="Phone"
          rules={[{ required: true, message: "Please input the phone number" }]}
        >
          <Input placeholder="Enter phone number" maxLength={10} />
        </Form.Item>

      </Form>
    );
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const { field, order } = sorter;
    const sortField = field || "";
    const sortOrder =
      order === "ascend" ? "asc" : order === "descend" ? "desc" : "";

    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });

    fetchUsers(
      pagination.current,
      pagination.pageSize,
      search,
      sortField,
      sortOrder
    );
  };

  const handleFilteredData = (data) => {
    if (data && Array.isArray(data.data)) {
      setUsersData(data.data);

      // Update pagination with the total count
      setPagination(prev => ({
        ...prev,
        total: data.total
      }));
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{ marginBottom: 16, display: "flex", flexDirection: "column" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0, color: isDarkMode ? "#ffffff" : undefined }}>
            Users
          </h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal("add")}
          >
            Add User
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <FilterComponent
            isDarkMode={isDarkMode}
            onSearch={(dates) => {
              setDateFilter(dates);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            onDataReceived={handleFilteredData}
          />
          <Input
            placeholder="Search users..."
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            value={search}
            onChange={(e) => {
              const value = e.target.value;
              setSearch(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            suffix={search && (
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => {
                  setSearch('');
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
              />
            )}
          />
        </div>
      </div>

      <Card
        style={{
          background: isDarkMode ? "#1f1f1f" : "#ffffff",
          borderColor: isDarkMode ? "#303030" : "#f0f0f0",
        }}
      >
        <Spin
          size="large"
          spinning={isDataLoading}
          style={{ display: "block", textAlign: "center", marginTop: "50px" }}
        >
          <Table
            columns={columns}
            dataSource={usersData}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: usersData.length, // Total number of data items
              showSizeChanger: true, // Allow page size change
              pageSizeOptions: ["10", "20", "30", "50"], // Options for page sizes
              onChange: (page, pageSize) => {
                setPagination({
                  current: page,
                  pageSize: pageSize,
                });
              },
            }}
            onChange={handleTableChange}
          />
        </Spin>
      </Card>

      <Modal
        title={
          modalMode === "add"
            ? "Add User"
            : modalMode === "edit"
              ? "Edit User"
              : "View User"
        }
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        {renderModalContent()}
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}
        >
          {modalMode !== "view" && (
            <Button
              type="primary"
              loading={loading}
              onClick={() => form.submit()}
            >
              {modalMode === "add" ? "Add User" : "Update User"}
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Users;
